import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminHeader from "./Header";
import { adminReqConfig, api, baseURL } from "../../include/api";
import { useNotification } from "../../Toast/ToastProvider";
import { ERROR, SUCCESS } from "../../include/constant";

const STATUS_COLORS = {
  approved: { color: "#46A27B", bg: "#D5F5E7", label: "Approved" },
  submitted: { color: "#CF9500", bg: "#FFF3BA", label: "Submitted" },
  rejected: { color: "#E54611", bg: "#FDDFD0", label: "Rejected" },
};

const formatAmount = (amount) => {
  const value = Number(amount);
  if (Number.isNaN(value)) return amount || "N/A";
  return value.toLocaleString("en-IN", { style: "currency", currency: "INR" });
};

const detailStyle = { color: "#697891", fontWeight: "500" };

const PaymentProofReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useNotification();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [proof, setProof] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const loadProof = useCallback(() => {
    setFetchLoading(true);
    api
      .get(`/payment_proof/?id=${id}`, adminReqConfig())
      .then((response) => {
        const row = response.status === 200 ? response.data?.data?.[0] : null;
        setProof(row || null);
        setRejectReason(row?.rejectReason || "");
      })
      .catch(() => {
        toastRef.current({ type: ERROR, message: "Failed to load payment proof." });
        setProof(null);
      })
      .finally(() => setFetchLoading(false));
  }, [id]);

  useEffect(() => {
    loadProof();
  }, [loadProof]);

  const reviewed = proof && proof.status !== "submitted";
  const statusStyle = STATUS_COLORS[proof?.status] || {
    color: "#333",
    bg: "#f5f5f5",
    label: proof?.status || "N/A",
  };

  const review = (action) => {
    if (action === "approve" && !isVerified) {
      toast({ type: ERROR, message: "Please verify the payment proof first." });
      return;
    }
    setSaving(true);
    const payload = { action };
    if (action === "reject") payload.rejectReason = rejectReason.trim();
    api
      .put(`/payment_proof/?id=${id}`, payload, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: response.data?.message || (action === "approve" ? "Payment confirmed" : "Payment proof rejected"),
          });
          setIsVerified(false);
          loadProof();
        }
      })
      .catch((error) => {
        toast({
          type: ERROR,
          message: error.response?.data?.message || "Unable to review payment proof.",
        });
      })
      .finally(() => setSaving(false));
  };

  const screenshotUrl = proof?.screenshotUrl ? `${baseURL}/${proof.screenshotUrl}` : "";

  return (
    <AdminHeader title={"PAYMENT PROOF"}>
      <div className="content-wrapper p-0">
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
            minHeight: "90vh",
          }}
        >
          {fetchLoading ? (
            <p>Loading payment proof...</p>
          ) : !proof ? (
            <div className="rounded-5 bg-white py-24 px-24">
              <p className="mb-3">Payment proof not found.</p>
              <button
                type="button"
                onClick={() => navigate("/admin/sales/paymentProof")}
                style={{
                  color: "#2692d2",
                  backgroundColor: "#d9ebff",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "700",
                  padding: "8px 24px",
                  border: "none",
                }}
              >
                Back to list
              </button>
            </div>
          ) : (
            <>
              <div className="rounded-5 bg-white py-24">
                <div className="row px-24 py-10 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-14">
                    <p className="fs-5 fw-bold text-dark mb-0">Payment Details</p>
                    <button
                      type="button"
                      onClick={() => navigate("/admin/sales/paymentProof")}
                      style={{
                        color: "#2692d2",
                        backgroundColor: "#d9ebff",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        padding: "8px 24px",
                        border: "none",
                      }}
                    >
                      Back
                    </button>
                  </div>
                  <div className="row mt-24 mb-20">
                    <div className="col-md-2">
                      <p className="fs-6 fw-bold text-dark">Proof ID</p>
                      <p className="fs-6" style={detailStyle}>{proof.id}</p>
                    </div>
                    <div className="col-md-2">
                      <p className="fs-6 fw-bold text-dark">Order ID</p>
                      <p className="fs-6" style={detailStyle}>{proof.orderId ?? "N/A"}</p>
                    </div>
                    <div className="col-md-2">
                      <p className="fs-6 fw-bold text-dark">Customer ID</p>
                      <p className="fs-6" style={detailStyle}>{proof.customerId ?? "N/A"}</p>
                    </div>
                    <div className="col-md-2">
                      <p className="fs-6 fw-bold text-dark">Amount</p>
                      <p className="fs-6" style={detailStyle}>{formatAmount(proof.amount)}</p>
                    </div>
                    <div className="col-md-2">
                      <p className="fs-6 fw-bold text-dark">UTR</p>
                      <p className="fs-6" style={detailStyle}>{proof.utr || "N/A"}</p>
                    </div>
                    <div className="col-md-2">
                      <p className="fs-6 fw-bold text-dark">UPI ID</p>
                      <p className="fs-6" style={detailStyle}>{proof.upiId || "N/A"}</p>
                    </div>
                  </div>
                  <div className="mt-10">
                    <p className="fs-6 fw-bold text-dark">Status</p>
                    <span
                      style={{
                        padding: "6px 12px",
                        border: `1px solid ${statusStyle.color}`,
                        borderRadius: "6px",
                        backgroundColor: statusStyle.bg,
                        fontWeight: "500",
                        color: statusStyle.color,
                        display: "inline-block",
                        fontSize: "14px",
                      }}
                    >
                      {statusStyle.label}
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-5 bg-white py-24 mt-24">
                <div className="px-24 py-10">
                  <p className="fs-5 fw-bold text-dark mb-14">Payment Screenshot</p>
                  <div
                    className="p-3"
                    style={{ border: "2px solid #DBE3F3", borderRadius: "5px", background: "#FAFAFD" }}
                  >
                    {screenshotUrl ? (
                      <a href={screenshotUrl} target="_blank" rel="noopener noreferrer">
                        <img
                          src={screenshotUrl}
                          alt="Payment screenshot"
                          style={{ maxWidth: "360px", width: "100%", borderRadius: "6px", objectFit: "contain" }}
                        />
                      </a>
                    ) : (
                      <p className="text-muted fs-6 mb-0">No screenshot attached.</p>
                    )}
                  </div>

                  {!reviewed && (
                    <div className="form-check mt-24">
                      <input
                        className="form-check-input"
                        style={{ borderColor: "#c5d5f5" }}
                        type="checkbox"
                        id="paymentVerified"
                        checked={isVerified}
                        onChange={(e) => setIsVerified(e.target.checked)}
                      />
                      <label className="form-check-label fs-6 fw-bold text-dark ms-14" htmlFor="paymentVerified">
                        Payment Verified
                      </label>
                    </div>
                  )}

                  {(!reviewed || proof.rejectReason) && (
                    <div className="mt-24" style={{ maxWidth: "520px" }}>
                      <label className="form-label fw-bold" htmlFor="rejectReason">
                        Reject reason
                      </label>
                      <textarea
                        id="rejectReason"
                        className="form-control"
                        rows="3"
                        value={rejectReason}
                        disabled={reviewed || saving}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Required only when rejecting"
                      />
                    </div>
                  )}

                  <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                    <button
                      type="button"
                      onClick={() => review("approve")}
                      disabled={saving || reviewed || !isVerified}
                      style={{
                        color: saving || reviewed || !isVerified ? "#999" : "#fff",
                        backgroundColor: saving || reviewed || !isVerified ? "#ccc" : "#2b95d3",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: saving || reviewed || !isVerified ? "not-allowed" : "pointer",
                        padding: "8px 36px",
                        height: "40px",
                        border: "none",
                      }}
                    >
                      {proof.status === "approved" ? "Already Accepted" : "Accept"}
                    </button>
                    <button
                      type="button"
                      onClick={() => review("reject")}
                      disabled={saving || reviewed}
                      style={{
                        color: saving || reviewed ? "#999" : "#2692d2",
                        backgroundColor: saving || reviewed ? "#eee" : "#d9ebff",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: saving || reviewed ? "not-allowed" : "pointer",
                        padding: "8px 36px",
                        height: "40px",
                        border: "none",
                      }}
                    >
                      {proof.status === "rejected" ? "Already Rejected" : "Reject"}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </AdminHeader>
  );
};

export default PaymentProofReview;
